/**
 * Normalize quotes in a suggestion to match the quote style of the original text.
 *
 * When the AI generates suggestions, it sometimes converts curly/smart quotes
 * to straight quotes, creating inconsistent formatting. This function detects
 * the quote style in the original and converts the suggestion to match.
 */

// Curly quote characters
const LEFT_DOUBLE_CURLY = "\u201c"; // "
const RIGHT_DOUBLE_CURLY = "\u201d"; // "
const LEFT_SINGLE_CURLY = "\u2018"; // '
const RIGHT_SINGLE_CURLY = "\u2019"; // '

// Straight quote characters
const STRAIGHT_DOUBLE = '"';
const STRAIGHT_SINGLE = "'";

/**
 * Detect whether a string uses curly quotes (smart quotes).
 * Returns true if curly quotes are found and no straight quotes are found,
 * or if curly quotes outnumber straight quotes.
 */
export function usesCurlyQuotes(text: string): boolean {
  const curlyCount =
    (text.match(/[\u201c\u201d\u2018\u2019]/g) || []).length;
  const straightDoubleCount =
    (text.match(/"/g) || []).length;
  const straightSingleInQuoteContext =
    (text.match(/(?<=[a-zA-Z])'(?=[a-zA-Z])/g) || []).length;
  // Don't count apostrophes as straight single quotes — only count
  // straight single quotes that appear to be used as quotation marks
  const straightSingleQuoteCount =
    (text.match(/'/g) || []).length - straightSingleInQuoteContext;

  if (curlyCount === 0) return false;
  if (straightDoubleCount === 0 && straightSingleQuoteCount <= 0) return true;

  // If both exist, curly quotes dominate
  return curlyCount > (straightDoubleCount + Math.max(0, straightSingleQuoteCount));
}

/**
 * Convert straight quotes in a string to curly/smart quotes.
 * Handles both double and single quotes with proper left/right orientation.
 */
function straightToCurly(text: string): string {
  let result = text;

  // Convert straight double quotes to curly
  // Track whether we expect an opening or closing quote
  let expectOpenDouble = true;
  result = result.replace(/"/g, () => {
    const replacement = expectOpenDouble ? LEFT_DOUBLE_CURLY : RIGHT_DOUBLE_CURLY;
    expectOpenDouble = !expectOpenDouble;
    return replacement;
  });

  // Convert straight single quotes to curly
  // This is trickier because apostrophes (it's, don't) use the same character.
  // We build a new string character by character to avoid offset issues from
  // the double-quote replacement above changing string length.
  let singleResult = "";
  for (let i = 0; i < result.length; i++) {
    if (result[i] !== STRAIGHT_SINGLE) {
      singleResult += result[i];
      continue;
    }

    const before = i > 0 ? result[i - 1] : "";
    const after = i < result.length - 1 ? result[i + 1] : "";

    // If between two word characters, it's an apostrophe (use right/closing curly)
    if (before && after && /\w/.test(before) && /\w/.test(after)) {
      singleResult += RIGHT_SINGLE_CURLY;
    }
    // If followed by a letter/digit, it's an opening quote
    else if (after && /\w/.test(after)) {
      singleResult += LEFT_SINGLE_CURLY;
    }
    // Otherwise it's a closing single quote (after word char, comma, etc.)
    else {
      singleResult += RIGHT_SINGLE_CURLY;
    }
  }
  result = singleResult;

  return result;
}

/**
 * Normalize quotes in a suggestion to match the original text's quote style.
 *
 * If the original text uses curly quotes and the suggestion uses straight quotes,
 * convert the suggestion's straight quotes to curly quotes.
 *
 * @param original - The original text from the document
 * @param suggestion - The AI-generated suggestion
 * @returns The suggestion with quotes normalized to match the original's style
 */
export function normalizeQuotes(original: string, suggestion: string): string {
  if (!original || !suggestion) return suggestion;

  const originalUsesCurly = usesCurlyQuotes(original);
  if (!originalUsesCurly) return suggestion;

  // Check if suggestion has straight quotes that need converting
  const hasStraightDoubleQuotes = suggestion.includes(STRAIGHT_DOUBLE);
  const hasStraightSingleQuotes = suggestion.includes(STRAIGHT_SINGLE);

  if (!hasStraightDoubleQuotes && !hasStraightSingleQuotes) return suggestion;

  return straightToCurly(suggestion);
}
